import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Dropdown,
  Popconfirm,
  Select,
  Table,
  Tooltip,
} from 'antd';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQDeleteBrandAPI,
  RQGetBrandAPI,
  RQUpdateBrandAPI,
} from '../../../../src/reactQuery/apis/rqgetProductBrand';
import {selectAuth} from '../../../../src/redux/selectors/authSelector';
import SearchBar from '../../../commonComponents/SeachBar/SearchBar';

import BrandModal from './BrandModal/BrandModal';

const {Option} = Select;

const ProductBrandScreen = () => {
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateBrandData, setUpdateBrandData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchText, setSearchText] = useState('');

  const {data: brandData = [], isLoading, refetch} = RQGetBrandAPI(token);
       
  const {mutate: updateBrand} = RQUpdateBrandAPI({
    onSuccess: () => {
      toast.success('Brand updated successfully');
      refetch();
      setIsModalVisible(false);
    },
    onError: error => {
      toast.error(error?.message || 'Something went wrong');
    },
  });

  const handleStatusChange = (record, newStatus) => {
    updateBrand({
      token,
      id: record.id,
      formData: {
        name: record.name,
        isActive: newStatus === 'ACTIVE',
      },
    });
  };

  const {mutate: deleteBrand} = RQDeleteBrandAPI({
    onSuccess: () => {
      toast.success('Brand deleted successfully');
      refetch();
    },
    onError: error => {
      toast.error(error?.message);
    },
  });

  const handleDeleteBrand = id => deleteBrand({token, id});
  const handleResetFilters = () => {
    setSelectedStatus(null);
    setSearchText('');
  };

  const handleSearch = text => setSearchText(text.trim().toLowerCase());
 
  const filteredData = brandData.filter(brand => {
    if (selectedStatus === 'ACTIVE' && !brand.isActive) {return false;}
    if (selectedStatus === 'INACTIVE' && brand.isActive) {return false;}
    
    if (searchText) {
      return brand.name.toLowerCase().includes(searchText);
    }

    return true;
  });

  const formattedBrandData = filteredData.map((brand, index) => ({
    key: brand.id,
    id: brand.id,
    srno: index + 1,
    name: brand.name,
    isActive: brand.isActive,
  }));

  const columns = [
    {title: 'Sr. No.', dataIndex: 'srno', width: 80},
    {title: 'Brand Name', dataIndex: 'name'},
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            onClick: e => handleStatusChange(record, e.key),
            items: [
              !isActive && {key: 'ACTIVE', label: 'Activate'},
              isActive && {key: 'INACTIVE', label: 'Deactivate'},
            ].filter(Boolean),
          }}
        >
          <span className={'cursor-pointer font-medium'}>
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </Dropdown>
      ),
    },
    {
      title: 'Action',
      width: 120,
      render: (_, record) => (
        <div className={'flex gap-6'}>
          <EditOutlined
            className={'cursor-pointer'}
            onClick={() => {
              setUpdateBrandData(record);
              setIsModalVisible(true);
            }}
          />
          <Tooltip title={'Delete brand'}>
            <Popconfirm
              title={'Are you sure you want to delete this brand?'}
              onConfirm={() => handleDeleteBrand(record.id)}
              okText={'Yes'}
              cancelText={'No'}
              placement={'top'}
            >
              <DeleteOutlined className={'text-red-500 cursor-pointer'} />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb className={'hidden lg:block'} items={[{title: 'Home'}, {title: 'Brand'}]} />

      <div className={'flex justify-between items-center mt-4'}>
        <h1 className={'font-bold text-lg'}>Brand List</h1>
        <Button
          type={'primary'}
          onClick={() => {
            setUpdateBrandData(null);
            setIsModalVisible(true);
          }}
        >
          <PlusOutlined /> Add Brand
        </Button>
      </div>

      <div className={'flex gap-2 mt-4'}>
        <SearchBar value={searchText} onSearch={handleSearch} placeholder={'Search brands...'} />
        <Select
          placeholder={'Select status'}
          allowClear
          className={'w-48'}
          value={selectedStatus}
          onChange={setSelectedStatus}
        >
          <Option value={'ACTIVE'}>Active</Option>
          <Option value={'INACTIVE'}>Inactive</Option>
        </Select>

        <Button onClick={handleResetFilters}>
          <ReloadOutlined /> Reset
        </Button>

      </div>

      <div className={'mt-4 overflow-x-auto'}>
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={formattedBrandData}
          pagination={{pageSize: 10}}
        />
      </div>

      <BrandModal
        brandList={brandData}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        refetch={refetch}
        updateBrandData={updateBrandData}
      />
    </div>
  );
};

export default ProductBrandScreen;
